import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Card, Space, Typography, Modal, Descriptions, Divider, message, Empty } from 'antd';
import { CheckCircleOutlined, CloseOutlined, DeleteOutlined, EditOutlined, } from '@ant-design/icons';
import { checkPermission } from '@/utils/permission';
import { approveOrthodonticExam, getOrthodonticExamByCustomer, getSpecificOrthodonticExam, removeOrthodonticExam, unapproveOrthodonticExam } from '@/redux/actions/orthodonticExamActions';
import { isValidDate, renderRank } from '@/utils/helpers';
import OrthodonticExamForm from './OrthodonticExamForm';
import PrintButton from '@/components/PrintButton/PrintButton';
import PrintOrthodonticExam from './PrintOrthodonticExam';

const { Text } = Typography;
const { confirm } = Modal;

const OrthodonticExamList = ({ customer, onCountChange }) => {
    const customerId = customer?.id;
    const dispatch = useDispatch();

    const [, setIsLoading] = useState(false);
    const [formVisible, setFormVisible] = useState(false);
    const [editingRecord, setEditingRecord] = useState(null);

    const allowUpdate = checkPermission('orthodontic_exams', 'update');
    const allowDelete = checkPermission('orthodontic_exams', 'remove');
    const allowApproved = checkPermission('orthodontic_exams', 'approve');
    const allowUnapproved = checkPermission('orthodontic_exams', 'unapprove');

    const orthodonticExamList = useSelector((s) => s.orthodonticExamReducer.orthodonticExamList) || [];

    const loadOrthodonticExams = async () => {
        try {
            setIsLoading(true);
            await dispatch(getOrthodonticExamByCustomer({}, customerId));
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (customerId) {
            loadOrthodonticExams();
        };
    }, [customerId]);

    const handleOpenEditForm = async (id) => {
        try {
            const res = await dispatch(getSpecificOrthodonticExam(id));
            setEditingRecord(res);
            setFormVisible(true);
        } catch (error) {
            message.error('Không thể tải dữ liệu phiếu khám tổng quát');
        }
    };

    const handleCancelForm = () => {
        setFormVisible(false);
        setEditingRecord(null);
    };

    const handleDelete = (exam) => {
        confirm({
            title: 'Xác nhận xoá',
            content: 'Bạn có chắc chắn muốn xoá phiếu khám chỉnh nha này không?',
            okText: 'Xoá',
            okType: 'danger',
            cancelText: 'Huỷ',
            onOk: async () => {
                await dispatch(removeOrthodonticExam([exam.id]));
                loadOrthodonticExams();
            },
        });
    };

    const handleApprove = async (exam) => {
        if (!exam?.id) {
            message.error("Chưa có phiếu khám chỉnh nha để xác nhận");
            return;
        }

        Modal.confirm({
            title: "Xác nhận",
            content: "Bạn có chắc muốn xác nhận phiếu khám chỉnh nha?",
            okText: "Đồng ý",
            cancelText: "Hủy",
            onOk: async () => {
                setIsLoading(true);
                try {
                    await dispatch(approveOrthodonticExam(exam.id));
                    await loadOrthodonticExams(); // refresh lại data
                } finally {
                    setIsLoading(false);
                }
            }
        });
    };

    const handleUnapprove = async (exam) => {
        if (!exam?.id) return;

        Modal.confirm({
            title: "Xác nhận",
            content: "Bạn có chắc muốn hủy xác nhận phiếu khám chỉnh nha?",
            okText: "Đồng ý",
            cancelText: "Hủy",
            onOk: async () => {
                setIsLoading(true);
                try {
                    await dispatch(unapproveOrthodonticExam(exam.id));
                    await loadOrthodonticExams(); // refresh lại data
                } finally {
                    setIsLoading(false);
                }
            }
        });
    };

    useEffect(() => {
        if (onCountChange) {
            onCountChange(orthodonticExamList?.length || 0);
        }
    }, [orthodonticExamList]);

    if (!orthodonticExamList?.length) {
        return (
            <Empty
                description={<span>Chưa có phiếu khám chỉnh nha nào.</span>}
                image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
        );
    }

    return (
        <div>
            {orthodonticExamList.map((exam, index) => {
                const isApproved = exam.is_approved || false;

                const ban_khoan_cua_benh_nhan = exam?.ban_khoan_cua_benh_nhan || '';
                const kich_thuoc_cung_rang = exam?.kich_thuoc_cung_rang || '';
                const ham_tren_muc_do_chen_chuc = exam?.ham_tren_muc_do_chen_chuc || '';
                const ham_duoi_muc_do_chen_chuc = exam?.ham_duoi_muc_do_chen_chuc || '';
                const khe_thua_ham_tren = exam?.khe_thua_ham_tren || [];
                const khe_thua_ham_duoi = exam?.khe_thua_ham_duoi || [];
                const thua_thieu_rang = exam?.thua_thieu_rang || [];
                const bat_thuong_rang_can_luu_y = exam?.bat_thuong_rang_can_luu_y || '';
                const do_trum = exam?.do_trum || '';
                const do_chia = exam?.do_chia || '';
                const rang_3_trai = exam?.rang_3_trai || [];
                const rang_6_trai = exam?.rang_6_trai || [];
                const rang_3_phai = exam?.rang_3_phai || [];
                const rang_6_phai = exam?.rang_6_phai || [];
                const duong_cong_spee = exam?.duong_cong_spee || '';
                const mat_phang_can = exam?.mat_phang_can || '';
                const loai_mo_nha_chu = exam?.loai_mo_nha_chu || '';
                const can_lech_chuc_nang = exam?.can_lech_chuc_nang || '';
                const khop_can_bat_thuong_can_luu_y = exam?.khop_can_bat_thuong_can_luu_y || '';
                const kich_thuoc_luoi = exam?.kich_thuoc_luoi || '';
                const tat_day_luoi = exam?.tat_day_luoi || '';
                const quan_sat_va_luu_y_khac_chinh_nha = exam?.quan_sat_va_luu_y_khac_chinh_nha || '';
                const dang_mat_thang = exam?.dang_mat_thang || '';
                const do_can_doi_cam = exam?.do_can_doi_cam || '';
                const tuong_quan_tang_mat_duoi = exam?.tuong_quan_tang_mat_duoi || '';
                const moi_khep_nghi = exam?.moi_khep_nghi || '';
                const ranh_moi_cam = exam?.ranh_moi_cam || '';
                const bat_thuong_mat_thang_nghi = exam?.bat_thuong_mat_thang_nghi || '';
                const duong_cuoi = exam?.duong_cuoi || '';
                const cuoi_lo_nuou = exam?.cuoi_lo_nuou || '';
                const hanh_lang_ma = exam?.hanh_lang_ma || '';
                const duong_giua = exam?.duong_giua || '';
                const bat_thuong_mat_thang_cuoi = exam?.bat_thuong_mat_thang_cuoi || '';
                const dang_mat_nghieng = exam?.dang_mat_nghieng || '';
                const goc_moi_tren = exam?.goc_moi_tren || '';
                const vi_tri_cam = exam?.vi_tri_cam || '';
                const goc_mat_phang_ham_duoi = exam?.goc_mat_phang_ham_duoi || '';
                const lom_moi_cam = exam?.lom_moi_cam || '';
                const hinh_thai_moi = exam?.hinh_thai_moi || '';
                const huong_dieu_tri_so_bo = exam?.huong_dieu_tri_so_bo || '';
                const created_at = exam?.created_at || '';

                return (
                    <Card
                        key={exam.id || index}
                        style={{
                            marginBottom: 24,
                            border: '1px solid transparent',
                            borderBottom: '5px solid transparent',
                            borderImage: 'linear-gradient(to right, #ff4d4f, #faad14, #52c41a)', // đổi màu ở đây
                            borderImageSlice: 1,
                            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                            borderRadius: 6,
                        }}
                        title={
                            <Space direction="vertical" size={0}>
                                <Text strong>Ngày khám: {isValidDate(created_at, true)}</Text>
                            </Space>
                        }
                        extra={
                            <Space>
                                <PrintButton printText="In" size="small">
                                    <PrintOrthodonticExam customer={customer} data={exam} />
                                </PrintButton>
                                <Button
                                    icon={<EditOutlined />}
                                    size="small"
                                    type="primary"
                                    onClick={() => handleOpenEditForm(exam.id)}
                                    disabled={!allowUpdate || isApproved}
                                >
                                    Sửa
                                </Button>
                                <Button
                                    icon={<DeleteOutlined />}
                                    size="small"
                                    danger
                                    onClick={() => handleDelete(exam)}
                                    disabled={!allowDelete || isApproved}
                                >
                                    Xóa
                                </Button>
                            </Space>
                        }
                    >
                        <Divider orientation="left" orientationMargin="0" className="custom-section-title">
                            I. BĂN KHOĂN CỦA BỆNH NHÂN
                        </Divider>
                        <Descriptions size="small" column={1}>
                            <Descriptions.Item>
                                {ban_khoan_cua_benh_nhan || '-'}
                            </Descriptions.Item>
                        </Descriptions>

                        <Divider
                            orientation="left"
                            orientationMargin="0"
                            className="mt-3 custom-section-title"
                        >II. KHÁM TRONG MIỆNG</Divider>
                        <Descriptions size="small" column={1}>
                            <Descriptions.Item label={<span className="description-parent-label">
                                1. Đánh giá tình trạng răng và cung răng
                            </span>}>
                            </Descriptions.Item>
                            <Descriptions.Item label="• Kích thước cung răng">
                                {kich_thuoc_cung_rang || "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="• Hàm trên: Mức độ chen chúc ước tính">
                                {ham_tren_muc_do_chen_chuc || "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="• Hàm dưới: Mức độ chen chúc ước tính">
                                {ham_duoi_muc_do_chen_chuc || "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="• Khe thưa hàm trên">
                                {renderRank(khe_thua_ham_tren)}
                            </Descriptions.Item>
                            <Descriptions.Item label="• Khe thưa hàm dưới">
                                {renderRank(khe_thua_ham_duoi)}
                            </Descriptions.Item>
                            <Descriptions.Item label="• Bệnh nhân có thiếu/thừa răng không?">
                                {renderRank(thua_thieu_rang)}
                            </Descriptions.Item>
                            <Descriptions.Item label="• Bất thường răng cần lưu ý">
                                {bat_thuong_rang_can_luu_y || "-"}
                            </Descriptions.Item>
                        </Descriptions>

                        <Descriptions size="small" column={1}>
                            <Descriptions.Item
                                label={<span className='description-parent-label'>2. Đánh giá tình trạng khớp cắn</span>}
                            />
                            <Descriptions.Item label="• Độ trùm">
                                {do_trum || "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="• Độ chìa">
                                {do_chia || "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="• Khớp cắn bên trái">
                                <div>• <span style={{ fontWeight: 500 }}>Răng 3</span> {renderRank(rang_3_trai, "mm", false)}</div>
                                <div>• <span style={{ fontWeight: 500 }}>Răng 6</span> {renderRank(rang_6_trai, "mm", false)}</div>
                            </Descriptions.Item>
                            <Descriptions.Item label="• Khớp cắn bên phải">
                                <div>• <span style={{ fontWeight: 500 }}>Răng 3</span> {renderRank(rang_3_phai, "mm", false)}</div>
                                <div>• <span style={{ fontWeight: 500 }}>Răng 6</span> {renderRank(rang_6_phai, "mm", false)}</div>
                            </Descriptions.Item>
                            <Descriptions.Item label="• Đường cong Spee">
                                {duong_cong_spee || "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="• Mặt phẳng cắn">
                                {renderRank(mat_phang_can)}
                            </Descriptions.Item>
                            <Descriptions.Item label="• Loại mô nha chu">
                                {loai_mo_nha_chu || "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="• Cắn lệch chức năng">
                                {can_lech_chuc_nang || "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="• Bất thường khớp cắn khác cần lưu ý">
                                {khop_can_bat_thuong_can_luu_y || "-"}
                            </Descriptions.Item>
                        </Descriptions>

                        <Descriptions size="small" column={1}>
                            <Descriptions.Item
                                label={<span className='description-parent-label'>3. Các ghi chú và quan sát khác</span>}
                            />
                            <Descriptions.Item label="• Kích thước lưỡi">
                                {kich_thuoc_luoi || "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="• Tật đẩy lưỡi">
                                {tat_day_luoi || "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="• Quan sát hoặc lưu ý khác của bác sĩ về bệnh nhân">
                                {quan_sat_va_luu_y_khac_chinh_nha || "-"}
                            </Descriptions.Item>
                        </Descriptions>

                        <Divider orientation="left" orientationMargin="0" className="mt-3 custom-section-title">
                            III. KHÁM NGOÀI MẶT
                        </Divider>
                        <Descriptions size="small" column={1}>
                            <Descriptions.Item
                                label={<span className='description-parent-label'>1. Đánh giá mặt thẳng ở tư thế nghỉ</span>}
                            />
                            <Descriptions.Item label="• Dạng mặt thẳng">
                                {dang_mat_thang || "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="• Độ cân đối của cằm">
                                {do_can_doi_cam || "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="• Tương quan tầng mặt dưới so với tổng thể khuôn mặt">
                                {tuong_quan_tang_mat_duoi || "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="• Môi có khép kín ở tư thế nghỉ hay không">
                                {moi_khep_nghi || "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="• Rãnh môi cằm">
                                {ranh_moi_cam || "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="• Ngoài ra có bất thường gì nổi trội khác hay không?">
                                {bat_thuong_mat_thang_nghi || "-"}
                            </Descriptions.Item>
                        </Descriptions>

                        <Descriptions size="small" column={1}>
                            <Descriptions.Item
                                label={<span className='description-parent-label'>2. Đánh giá mặt thẳng ở tư thế cười</span>}
                            />
                            <Descriptions.Item label="• Đường cười">
                                {duong_cuoi || "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="• Cười lộ nướu">
                                {renderRank(cuoi_lo_nuou, "mm", false)}
                            </Descriptions.Item>
                            <Descriptions.Item label="• Hành lang má">
                                {hanh_lang_ma || "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="• Đường giữa">
                                {renderRank(duong_giua, "mm", false)}
                            </Descriptions.Item>
                            <Descriptions.Item label="• Ngoài ra có bất thường gì nổi trội khác hay không?">
                                {bat_thuong_mat_thang_cuoi || "-"}
                            </Descriptions.Item>
                        </Descriptions>

                        <Descriptions size="small" column={1}>
                            <Descriptions.Item
                                label={<span className='description-parent-label'>3. Đánh giá mặt nghiêng ở tư thế nghỉ</span>}
                            />
                            <Descriptions.Item label="• Dạng mặt nghiêng">
                                {dang_mat_nghieng || '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label="• Góc môi trên">
                                {goc_moi_tren || '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label="• Vị trí cằm">
                                {vi_tri_cam || '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label="• Góc mặt phẳng hàm dưới">
                                {goc_mat_phang_ham_duoi || '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label="• Lõm môi cằm">
                                {lom_moi_cam || '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label="• Hình thái môi">
                                {hinh_thai_moi || '-'}
                            </Descriptions.Item>
                        </Descriptions>

                        <Divider orientation="left" orientationMargin="0" className="mt-3 custom-section-title">
                            IV. HƯỚNG ĐIỀU TRỊ SƠ BỘ
                        </Divider>
                        <Descriptions size="small" column={1}>
                            <Descriptions.Item>
                                {huong_dieu_tri_so_bo || '-'}
                            </Descriptions.Item>
                        </Descriptions>

                        <Divider orientation="left" orientationMargin="0" className="mt-3 custom-section-title">
                            V. THÔNG TIN NHÂN SỰ
                        </Divider>
                        <Descriptions size="small" column={1}>
                            <Descriptions.Item label="Người tạo phiếu">
                                {exam.created_user?.user_role?.name || "-"} - {exam.created_user?.full_name || "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="Bác sĩ phụ trách">
                                {exam.doctor?.full_name || "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="Y tá phụ trách">
                                {Array.isArray(exam.y_ta_phu_trach) && exam.y_ta_phu_trach.length > 0
                                    ? exam.y_ta_phu_trach.join(", ")
                                    : "-"}
                            </Descriptions.Item>
                        </Descriptions>

                        <div className='mt-4'>
                            {isApproved ? (
                                <>
                                    <Space align="center" size="middle">
                                        {allowUnapproved && (
                                            <Button
                                                htmlType="button"
                                                icon={<CloseOutlined />}
                                                onClick={() => handleUnapprove(exam)}
                                                danger
                                                size="large"
                                            >
                                                Hủy xác nhận của bác sĩ
                                            </Button>
                                        )}

                                        <Text style={{ color: 'rgba(0, 0, 0, 0.45)', fontStyle: 'italic' }}>
                                            Xác nhận bởi: {exam?.approved_by?.user_role?.name} - {exam?.approved_by?.full_name} - {isValidDate(exam?.approved_at, true)}
                                        </Text>
                                    </Space>
                                </>
                            ) : (
                                allowApproved && (
                                    <Button
                                        type="primary"
                                        style={{ background: "#52c41a", borderColor: "#52c41a" }}
                                        icon={<CheckCircleOutlined />}
                                        onClick={() => handleApprove(exam)}
                                        size="large"
                                        disabled={false}
                                    >
                                        Xác nhận của bác sĩ
                                    </Button>
                                )
                            )}
                        </div>
                    </Card>
                );
            })}

            <OrthodonticExamForm
                customer={customer}
                visible={formVisible}
                record={editingRecord}
                onCancel={handleCancelForm}
            />
        </div>
    );
};

export default OrthodonticExamList;
